import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import GroupedBarChart from '../components/charts/GroupedBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, IdCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Tabs ─────────────────────────────────────────────────────

const subTabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

// type sent to /api/azure/recommendations, keyed by sub-tab index
const TAB_TYPES = ['COMBINED_PUBLIC_IP', 'CUSTOM_PUBLIC_IP', 'PUBLIC_IP'];

// ── Rec helpers ──────────────────────────────────────────────

const resolveIPRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (['RELEASE', 'DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'].includes(t)) recPillClass = 'rec-id';
  return { recType: t || 'OPTIMAL', recPillClass };
};

const notNA = (v) => (v != null && String(v).trim() !== '' && String(v).trim().toUpperCase() !== 'NA') ? v : null;

const RELEASE_RECS = ['RELEASE', 'DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'];

// ── Row Mapper ───────────────────────────────────────────────
// All three tabs share one item shape; source-specific keys (publicIpName,
// resourceLocation, ipAllocationMethod…) are read defensively.

const toIPRow = (item) => {
  const current     = parseCost(item.currentMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue);
  const saving      = parseCost(item.savingCost ?? Math.max(0, current - recommended));

  const rawRec = item.recommendationType ?? (saving > 0 ? 'RELEASE' : 'OPTIMAL');
  const { recType, recPillClass } = resolveIPRec(rawRec);

  // Show backend detail exactly; NA / absent falls through, finally "—".
  const remark =
    notNA(item.recommendationDetail) ??
    notNA(item.remark) ??
    notNA(item.recommendationSubCategory) ??
    '—';

  const assoc      = item.associatedWith ?? '';
  const assocClean = assoc.toLowerCase().trim();
  const rawStatus  = item.status ?? null;

  const isAssociated =
    (rawStatus ?? '').toLowerCase() === 'associated' ||
    (assocClean !== '' && !['not attached', 'none', 'unassociated'].includes(assocClean));

  let status = rawStatus;
  if (!status) status = isAssociated ? 'Associated' : (RELEASE_RECS.includes(recType) ? 'Unassociated' : '—');

  return {
    id: item.id,
    name: item.name ?? item.publicIpName ?? '—',
    ipAddress: item.ipAddress ?? '—',
    location: item.location ?? item.resourceLocation ?? '—',
    resourceGroup: item.resourceGroup ?? '—',
    sku: item.skuName ?? item.sku ?? '—',
    skuTier: item.skuTier ?? '—',
    allocationMethod: item.publicIPAllocationMethod ?? item.ipAllocationMethod ?? '—',
    ipVersion: item.publicIPAddressVersion ?? '—',
    idleTimeout: item.idleTimeoutInMinutes ?? null,
    impact: item.impact ?? '—',
    provisioningState: item.provisioningState ?? '—',
    tagCoverage: item.tagCoverage ?? (item.isTagged ? 'Tagged' : '—'),
    associatedWith: assoc,
    status,
    isUnassociated: !isAssociated,
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: `$${recommended.toFixed(2)}`,
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving: formatSavingStr(saving),
    savingValue: saving,
    recommendedBy: item.recommendedBy ?? '—',
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
  };
};

const mapRows = (data) => (data ?? []).map(toIPRow);

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, rows) => {
  const rowCurrent = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec     = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const rowSaving  = rows.reduce((s, r) => s + r.savingValue, 0);

  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : rowSaving;

  const unassocRows  = rows.filter((r) => r.isUnassociated);
  const assocCount   = rows.length - unassocRows.length;
  const unassocCost  = unassocRows.reduce((s, r) => s + r.costValue, 0);

  return buildBaseCostKpis({
    countTitle: 'Total Public IPs',
    countValue: rows.length,
    countSubtitle: `${assocCount} associated · ${unassocRows.length} unassociated`,
    current, saving, recommended,
    kpi4Title: 'Unassociated (waste)',
    kpi4Value: `${unassocRows.length} IPs`,
    kpi4Subtitle: `$${unassocCost.toFixed(2)}/mo pure waste`,
  });
};

// ── Chart builders ────────────────────────────────────────────

const ASSOC_KEYS   = ['Unassociated', 'Load Balancer', 'NIC', 'NAT Gateway', 'Bastion', 'Other'];
const ASSOC_COLORS = ['#ef4444', '#0078d4', '#2ea66f', '#f57c00', '#26c6da', '#8e24aa'];

const bucketOfAssoc = (r) => {
  if (r.isUnassociated) return 'Unassociated';
  const a = (r.associatedWith ?? '').toLowerCase();
  if (a.includes('load') || a.includes('lb'))              return 'Load Balancer';
  if (a.includes('nic') || a.includes('network interface')) return 'NIC';
  if (a.includes('nat'))                                    return 'NAT Gateway';
  if (a.includes('bastion'))                                return 'Bastion';
  return 'Other';
};

const buildAssocDonut = (rows) => {
  if (!rows.length) return null;
  const hasCost = rows.some((r) => r.costValue > 0);
  const buckets = {};
  rows.forEach((r) => {
    const b = bucketOfAssoc(r);
    buckets[b] = (buckets[b] || 0) + (hasCost ? r.costValue : 1);
  });
  const labels = ASSOC_KEYS.filter((k) => buckets[k] > 0);
  if (!labels.length) return null;
  return {
    labels,
    datasets: [{
      data: labels.map((k) => parseFloat(buckets[k].toFixed(2))),
      backgroundColor: labels.map((k) => ASSOC_COLORS[ASSOC_KEYS.indexOf(k)]),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
};

const buildCostVsSavingsByLocation = (rows) => {
  if (!rows.length) return null;
  const byLocation = {};
  rows.forEach((r) => {
    if (!r.location || r.location === '—') return;
    if (!byLocation[r.location]) byLocation[r.location] = { cost: 0, saving: 0 };
    byLocation[r.location].cost   += r.costValue;
    byLocation[r.location].saving += r.savingValue;
  });
  const sorted = Object.entries(byLocation).sort((a, b) => b[1].cost - a[1].cost).slice(0, 6);
  if (!sorted.length) return null;
  return {
    labels: sorted.map(([loc]) => loc),
    datasets: [
      { label: 'Cost',    data: sorted.map(([, v]) => parseFloat(v.cost.toFixed(2))),   backgroundColor: '#4a90d9', borderRadius: 4, barPercentage: 0.7 },
      { label: 'Savings', data: sorted.map(([, v]) => parseFloat(v.saving.toFixed(2))), backgroundColor: '#2ea66f', borderRadius: 4, barPercentage: 0.7 },
    ],
  };
};

const buildStatusDonut = (rows) => {
  if (!rows.length) return null;
  const assoc = rows.filter((r) => !r.isUnassociated).length;
  const unassoc = rows.length - assoc;
  const entries = [['Associated', assoc], ['Unassociated', unassoc]].filter(([, v]) => v > 0);
  if (!entries.length) return null;
  return {
    labels: entries.map(([k]) => k),
    datasets: [{
      data: entries.map(([, v]) => v),
      backgroundColor: entries.map(([k]) => (k === 'Associated' ? '#2ea66f' : '#ef4444')),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
};

const buildSavingsByAction = (rows) => {
  const buckets = { 'Release unassociated': 0, 'Other': 0 };
  rows.forEach((r) => {
    const s = r.savingValue;
    if (s <= 0) return;
    if (RELEASE_RECS.includes(r.recType)) buckets['Release unassociated'] += s;
    else buckets['Other'] += s;
  });
  if (!Object.values(buckets).some((v) => v > 0)) return null;
  const labels = Object.keys(buckets);
  return {
    labels,
    datasets: [{
      label: 'Saving ($)',
      data: labels.map((k) => parseFloat(buckets[k].toFixed(2))),
      backgroundColor: ['#ef4444', '#f57c00'],
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows
    .filter((r) => r.savingValue > 0)
    .sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    const isRelease = RELEASE_RECS.includes(r.recType);
    return {
      id: i + 1,
      title: r.ipAddress || r.name || r.id,
      amount: r.saving,
      desc: `${r.location} · ${r.status}`,
      actionLabel: isRelease ? 'Release →' : 'Review →',
      actionClass: isRelease ? 'mini-btn ghost' : 'mini-btn',
      row: r,
    };
  });
  return { actions, totalSaving };
};

// ── Local cell components ─────────────────────────────────────

const StatusCell = ({ row }) => {
  const s = (row.status ?? '').toLowerCase();
  const isAssoc = s === 'associated';
  const isUnassoc = s.includes('unassoc');
  return (
    <span className={`status-pill ${isAssoc ? 'st-running' : isUnassoc ? 'st-stopped' : 'st-unknown'}`}>
      {row.status || '—'}
    </span>
  );
};

// ── Table columns ─────────────────────────────────────────────

const ipColumns = [
  { key: 'id',            label: 'Id', render: (row) => <IdCell row={row} /> },
  { key: 'name',          label: 'Name' },
  { key: 'ipAddress',     label: 'IP Address' },
  { key: 'location',      label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group' },
  {
    key: 'sku', label: 'SKU',
    render: (row) => (row.skuTier && row.skuTier !== '—') ? `${row.sku} (${row.skuTier})` : row.sku,
  },
  { key: 'allocationMethod', label: 'Allocation' },
  {
    key: 'associatedWith', label: 'Associated With',
    render: (row) => row.associatedWith
      ? <span title={row.associatedWith}>{row.associatedWith}</span>
      : <span style={{ color: 'var(--muted)' }}>None</span>,
  },
  { key: 'status', label: 'Status', render: (row) => <StatusCell row={row} /> },
  { key: 'impact', label: 'Impact' },
  { key: 'cost',                 label: 'Cost / Mo',         className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation',    render: (row) => <RecCell row={row} /> },
  { key: 'saving',               label: 'Saving / Mo',       className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',      label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action', label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main component ────────────────────────────────────────────

const AzurePublicIPOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom,   setIsLoadingCustom]   = useState(false);
  const [isLoadingAzure,    setIsLoadingAzure]    = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows,   setCustomRows]   = useState([]);
  const [azureRows,    setAzureRows]    = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi,   setCustomKpi]   = useState([]);
  const [azureKpi,    setAzureKpi]    = useState([]);

  const [assocData,      setAssocData]      = useState(null);
  const [costSavingsData, setCostSavingsData] = useState(null);
  const [statusData,     setStatusData]     = useState(null);
  const [savingsData,    setSavingsData]    = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal,   setInsightTotal]   = useState(0);

  const [tagList,           setTagList]           = useState([]);
  const [filterList,        setFilterList]        = useState({ tag: '', values: [] });
  const [locationFilter,    setLocationFilter]    = useState('');
  const [skuFilter,         setSkuFilter]         = useState('');
  const [associationFilter, setAssociationFilter] = useState('');
  const [statusFilter,      setStatusFilter]      = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      ipId: row.id, name: row.name, ipAddress: row.ipAddress, location: row.location,
      resourceGroup: row.resourceGroup, sku: row.sku, allocationMethod: row.allocationMethod,
      associatedWith: row.associatedWith, status: row.status,
      currentMonthlyCost: row.cost, recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the public IP data provided, give a concise insight directly to the ' +
      'user — mention unused IPs, potential savings, and what action they should take. Keep it conversational and ' +
      'to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchTagList = () => {
    if (!selectedAccount) return;
    setTagList([]);
    if (filterList.tag !== '' || filterList.values.length > 0) setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: 'PUBLIC_IP', userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchCombinedData = (userId) => {
    setIsLoadingCombined(true);
    setCombinedRows([]);
    RecommendationApiService.getAzureData({ userId, type: TAB_TYPES[0], userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapRows(res?.data);
        setCombinedRows(rows);
        setCombinedKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching combined public IP recommendations', err))
      .finally(() => setIsLoadingCombined(false));
  };

  const fetchCustomData = (userId) => {
    setIsLoadingCustom(true);
    setCustomRows([]);
    RecommendationApiService.getAzureData({
      userId, type: TAB_TYPES[1], userAccountId: selectedAccount,
      filterKey: filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    })
      .then((res) => {
        const rows = mapRows(res?.data);
        setCustomRows(rows);
        setCustomKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching custom public IP recommendations', err))
      .finally(() => setIsLoadingCustom(false));
  };

  const fetchAzureData = (userId) => {
    setIsLoadingAzure(true);
    setAzureRows([]);
    RecommendationApiService.getAzureData({ userId, type: TAB_TYPES[2], userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapRows(res?.data);
        setAzureRows(rows);
        setAzureKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching azure public IP recommendations', err))
      .finally(() => setIsLoadingAzure(false));
  };

  // Lazy fetch: only hits the API for a tab the first time it is opened.
  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    const userId = getUserId();
    if (!userId) return;

    if (tabIndex === 0) fetchCombinedData(userId);
    else if (tabIndex === 1) fetchCustomData(userId);
    else if (tabIndex === 2) fetchAzureData(userId);
  };

  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i);
  };

  const handleRefresh = () => {
    fetchedTabsRef.current.delete(activeTab);
    fetchTabData(activeTab, true);
    fetchTagList();
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) {
      setFilterList({ tag: '', values: [] });
      fetchedTabsRef.current = new Set();
      setActiveTab(0);
      fetchTabData(0);
      fetchTagList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // CloudsAnalytics (custom) tag filters are server-side — refetch only while its tab is active.
  useEffect(() => {
    if (activeTab !== 1 || !selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) {
      const userId = getUserId();
      if (userId) {
        fetchedTabsRef.current.add(1);
        fetchCustomData(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const activeKpi       = activeTab === 0 ? combinedKpi       : activeTab === 1 ? customKpi       : azureKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAzure;
  const baseRows        = activeTab === 0 ? combinedRows      : activeTab === 1 ? customRows      : azureRows;

  const locations = useMemo(() => [...new Set(baseRows.map((r) => r.location).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const skus      = useMemo(() => [...new Set(baseRows.map((r) => r.sku).filter((v) => v && v !== '—'))].sort(), [baseRows]);

  const filteredRows = useMemo(() => {
    let rows = baseRows;
    if (locationFilter) rows = rows.filter((r) => r.location === locationFilter);
    if (skuFilter)      rows = rows.filter((r) => r.sku === skuFilter);
    if (associationFilter) {
      if (associationFilter === 'None') rows = rows.filter((r) => r.isUnassociated);
      else rows = rows.filter((r) => (r.associatedWith ?? '').toLowerCase().includes(associationFilter.toLowerCase()));
    }
    if (statusFilter) rows = rows.filter((r) => (r.status ?? '').toLowerCase().includes(statusFilter.toLowerCase()));
    return rows;
  }, [baseRows, locationFilter, skuFilter, associationFilter, statusFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setAssocData(buildAssocDonut(filteredRows));
      setCostSavingsData(buildCostVsSavingsByLocation(filteredRows));
      setStatusData(buildStatusDonut(filteredRows));
      setSavingsData(buildSavingsByAction(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setAssocData(null); setCostSavingsData(null); setStatusData(null); setSavingsData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [filteredRows]);

  // Keep AI chatbot context in sync with what's on screen
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Public IP Optimization',
      kpi: activeKpi,
      publicIpRecommendations: filteredRows,
    });
  }, [filteredRows, activeKpi]);

  const handleExport = () =>
    exportCSV(activeRows, ipColumns.filter((c) => c.key !== 'action'), `azure-public-ip-tab${activeTab}.csv`);

  const clearLocalFilters = () => {
    setLocationFilter(''); setSkuFilter(''); setAssociationFilter(''); setStatusFilter('');
    setFilterList({ tag: '', values: [] });
  };

  const showClear = !!(filterList.tag || locationFilter || skuFilter || associationFilter || statusFilter);

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Public IP</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Public IP Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{baseRows.length} addresses</span>}
          </h1>
          <div className="meta">
            Azure Public IP Addresses · release unassociated IPs, reduce waste, improve governance
          </div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      {/* Charts */}
      <div className="grid-3" style={{ marginTop: '4px' }}>
        <Card className="first" title="Status & association" subtitle="IP distribution by association type">
          <DonutChart data={assocData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Cost vs savings by location" subtitle="Where idle IP spend sits">
          <GroupedBarChart data={costSavingsData} loading={isLoadingActive} ySuffix="" yPrefix="$" />
        </Card>
        <TopActionsCard
          title="Top IP actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoadingActive}
          entityName="addresses"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      {/* Sub-tabs */}
      <div className="sub-tabs">
        {subTabs.map((t, i) => (
          <div key={i} className={`sub-tab${activeTab === i ? ' active' : ''}`} onClick={() => handleTabChange(i)}>
            {t.label}
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label>SKU</label>
          <select value={skuFilter} onChange={(e) => setSkuFilter(e.target.value)}>
            <option value="">All</option>
            {skus.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Association</label>
          <select value={associationFilter} onChange={(e) => setAssociationFilter(e.target.value)}>
            <option value="">All</option>
            <option value="NIC">NIC</option>
            <option value="Load Balancer">Load Balancer</option>
            <option value="NAT Gateway">NAT Gateway</option>
            <option value="Bastion">Bastion</option>
            <option value="None">None</option>
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="Associated">Associated</option>
            <option value="Unassociated">Unassociated</option>
          </select>
        </div>
        <div className="spacer" />
        {showClear && <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>}
        <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
      </div>

      <Table
        rows={activeRows}
        columns={ipColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No Public IP recommendations"
      />

      {/* Bottom charts */}
      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Recommended savings by action" subtitle="Where the savings come from">
          <HorizontalBarChart data={savingsData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Associated vs unassociated" subtitle="How many IPs are actually in use">
          <DonutChart data={statusData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default AzurePublicIPOptimization;
